<template>
  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Single Infrastructure Setup</p>
        <h1 class="page-title">Create one complete workspace</h1>
        <p class="page-subtitle">
          A guided version of bulk setup: prepare the admin account, organization, clinic/hospital, then create everything in the correct backend order.
        </p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/bulk-setup')">
        <i class="fas fa-layer-group"></i> Bulk setup
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div class="setup-steps">
          <button class="setup-step" :class="{ active: currentStep === 1, done: currentStep > 1 }" @click="goToStep(1)">1 Account</button>
          <button class="setup-step" :class="{ active: currentStep === 2, done: currentStep > 2 }" @click="goToStep(2)">2 Organization</button>
          <button class="setup-step" :class="{ active: currentStep === 3, done: currentStep > 3 }" @click="goToStep(3)">3 Clinic / Hospital</button>
          <button class="setup-step" :class="{ active: currentStep === 4 }" @click="goToStep(4)">4 Review</button>
        </div>

        <div v-if="error" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ error }}
        </div>

        <section v-if="currentStep === 1">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Admin account</h2>
              <p class="section-description">This person becomes the clinic admin for the new organization and location.</p>
            </div>
          </div>
          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Full name <span class="required">*</span></label>
              <input class="ui-input" v-model="userData.full_name" placeholder="e.g. Nur Aisyah Ahmad">
            </div>
            <div class="form-section">
              <label class="field-label">Email <span class="required">*</span></label>
              <input type="email" class="ui-input" v-model="userData.email" placeholder="name@clinic.com" @input="syncUsername">
            </div>
            <div class="form-section">
              <label class="field-label">Username <span class="required">*</span></label>
              <input class="ui-input" v-model="userData.username" placeholder="Auto-generated from email" @input="noSpaces">
            </div>
            <div class="form-section">
              <label class="field-label">Temporary password <span class="required">*</span></label>
              <div class="input-with-action">
                <input :type="showPassword ? 'text' : 'password'" class="ui-input" v-model="userData.password" placeholder="Set a temporary password">
                <button type="button" class="input-action" @click="showPassword = !showPassword">
                  <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                </button>
              </div>
            </div>
            <div class="form-section">
              <label class="field-label">Contact number</label>
              <input class="ui-input" v-model="userData.contact_number" placeholder="Optional" @input="cleanPhone">
            </div>
            <div class="form-section">
              <label class="field-label">Profession</label>
              <input class="ui-input" v-model="userData.profession" placeholder="Administrator">
            </div>
          </div>
        </section>

        <section v-if="currentStep === 2">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Organization</h2>
              <p class="section-description">The parent workspace. Address and subscription details are optional.</p>
            </div>
          </div>
          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Organization name <span class="required">*</span></label>
              <input class="ui-input" v-model="orgData.organization_name" placeholder="e.g. Dr Mata Group" @input="syncClinicName">
            </div>
            <div class="form-section">
              <label class="field-label">Organization type <span class="required">*</span></label>
              <select class="ui-select" v-model="orgData.organization_type">
                <option value="clinic">Clinic group</option>
                <option value="hospital">Hospital group</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Contact email</label>
              <input type="email" class="ui-input" v-model="orgData.contact_email" placeholder="Defaults to admin email">
            </div>
            <div class="form-section">
              <label class="field-label">Subscription type</label>
              <select class="ui-select" v-model="orgData.subscription_type">
                <option value="">Not decided yet</option>
                <option value="Private">Private</option>
                <option value="Government">Government</option>
              </select>
            </div>
            <div class="form-section full-width">
              <label class="field-label">HQ address</label>
              <textarea class="ui-textarea" v-model="orgData.hq_address" placeholder="Optional headquarters address"></textarea>
            </div>
          </div>
        </section>

        <section v-if="currentStep === 3">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Clinic / hospital location</h2>
              <p class="section-description">This location will be attached to the organization and assigned to the admin account.</p>
            </div>
            <label class="copy-toggle">
              <input type="checkbox" v-model="copyOrgAddress" @change="copyAddressFromOrg">
              Use organization address
            </label>
          </div>
          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Clinic / hospital name <span class="required">*</span></label>
              <input class="ui-input" v-model="clinicData.clinic_name" placeholder="e.g. Dr Mata Bangsar">
            </div>
            <div class="form-section">
              <label class="field-label">Facility type</label>
              <select class="ui-select" v-model="clinicData.facility_type">
                <option value="clinic">Clinic</option>
                <option value="hospital">Hospital</option>
              </select>
            </div>
            <div class="form-section full-width">
              <label class="field-label">Address</label>
              <textarea class="ui-textarea" v-model="clinicData.address" placeholder="Optional clinic/hospital address"></textarea>
            </div>
            <div class="form-section">
              <label class="field-label">City</label>
              <input class="ui-input" v-model="clinicData.city">
            </div>
            <div class="form-section">
              <label class="field-label">State</label>
              <select class="ui-select" v-model="clinicData.state">
                <option value="">Select state</option>
                <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Postal code</label>
              <input class="ui-input" v-model="clinicData.postal_code">
            </div>
          </div>
        </section>

        <section v-if="currentStep === 4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Review package</h2>
              <p class="section-description">Creation order is organization → clinic/hospital → account assignment.</p>
            </div>
          </div>
          <div class="review-grid">
            <div class="review-card">
              <span>Account</span>
              <strong>{{ userData.full_name || 'Missing admin name' }}</strong>
              <small>{{ userData.email || 'Missing email' }}</small>
            </div>
            <div class="review-card">
              <span>Organization</span>
              <strong>{{ orgData.organization_name || 'Missing organization' }}</strong>
              <small>{{ orgData.organization_type }}</small>
            </div>
            <div class="review-card">
              <span>Clinic / Hospital</span>
              <strong>{{ clinicData.clinic_name || 'Missing location' }}</strong>
              <small>{{ clinicData.state || 'State optional' }}</small>
            </div>
          </div>
        </section>

        <div class="ui-actions">
          <button class="ui-button secondary" v-if="currentStep > 1" @click="currentStep--">
            <i class="fas fa-arrow-left"></i> Back
          </button>
          <button class="ui-button primary" v-if="currentStep < 4" @click="nextStep">
            Next <i class="fas fa-arrow-right"></i>
          </button>
          <button class="ui-button primary" v-if="currentStep === 4" @click="submitAll" :disabled="isSubmitting">
            <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-check'"></i>
            {{ isSubmitting ? 'Creating...' : 'Complete setup' }}
          </button>
        </div>
      </div>
    </div>

    <transition name="fading">
      <Loading v-if="isSubmitting"/>
    </transition>
  </div>
</template>

<script>
import Loading from "@/components/Loading.vue"
import StatesJSON from "@/constants/states"
import { registerOrg } from "@/services/orgServices"
import { registerClinics } from "@/services/clinicServices"
import { registerUser } from "@/services/userServices"

export default {
  components: {
    Loading
  },
  data() {
    return {
      currentStep: 1,
      isSubmitting: false,
      error: "",
      states: StatesJSON,
      showPassword: false,
      copyOrgAddress: true,
      orgData: {
        organization_name: "",
        organization_type: "clinic",
        hq_address: "",
        city: "",
        state: "",
        postal_code: "",
        contact_email: "",
        contact_phone: "",
        subscription_type: "Private",
        country: "Malaysia"
      },
      clinicData: {
        clinic_name: "",
        facility_type: "clinic",
        address: "",
        city: "",
        state: "",
        postal_code: "",
        country: "Malaysia",
        latitude: "",
        longitude: ""
      },
      userData: {
        username: "",
        email: "",
        password: "",
        full_name: "",
        contact_number: "",
        profession: "Administrator",
        role: "clinic_admin",
        specialization: "Administration",
        locations: [],
        OrganizationUuid: ""
      }
    }
  },
  methods: {
    goToStep(step) {
      if (step <= this.currentStep) {
        this.currentStep = step;
      }
    },
    noSpaces() {
      this.userData.username = this.userData.username.replace(/\s/g, "").toLowerCase();
    },
    syncUsername() {
      if (!this.userData.username && this.userData.email.includes("@")) {
        this.userData.username = this.userData.email.split("@")[0].replace(/\s/g, "").toLowerCase();
      }
      if (!this.orgData.contact_email) {
        this.orgData.contact_email = this.userData.email;
      }
    },
    cleanPhone() {
      this.userData.contact_number = this.userData.contact_number.replace(/\D/g, "");
      this.orgData.contact_phone = this.userData.contact_number;
    },
    syncClinicName() {
      if (!this.clinicData.clinic_name && this.orgData.organization_name) {
        this.clinicData.clinic_name = `${this.orgData.organization_name} HQ`;
      }
    },
    copyAddressFromOrg() {
      if (!this.copyOrgAddress) return;
      this.clinicData.address = this.orgData.hq_address;
      this.clinicData.city = this.orgData.city;
      this.clinicData.state = this.orgData.state;
      this.clinicData.postal_code = this.orgData.postal_code;
    },
    validateStep1() {
      if (!this.userData.full_name || !this.userData.email || !this.userData.username || !this.userData.password) {
        this.error = "Complete the required account fields first.";
        return false;
      }
      this.error = "";
      return true;
    },
    validateStep2() {
      if (!this.orgData.organization_name || !this.orgData.organization_type) {
        this.error = "Organization name and type are required.";
        return false;
      }
      this.error = "";
      return true;
    },
    validateStep3() {
      if (!this.clinicData.clinic_name) {
        this.error = "Clinic or hospital name is required.";
        return false;
      }
      this.error = "";
      return true;
    },
    nextStep() {
      if (this.currentStep === 1 && this.validateStep1()) {
        this.currentStep++;
      } else if (this.currentStep === 2 && this.validateStep2()) {
        this.syncClinicName();
        this.copyAddressFromOrg();
        this.currentStep++;
      } else if (this.currentStep === 3 && this.validateStep3()) {
        this.currentStep++;
      }
    },
    getUuid(response, keys) {
      for (const key of keys) {
        if (response?.[key]) return response[key];
        if (response?.data?.[key]) return response.data[key];
      }
      return "";
    },
    async submitAll() {
      if (!this.validateStep1() || !this.validateStep2() || !this.validateStep3()) {
        return;
      }

      this.isSubmitting = true;
      this.error = "";

      try {
        const orgResponse = await registerOrg(this.orgData);
        const orgUuid = this.getUuid(orgResponse, ["OrganizationUuid", "organization_uuid", "uuid"]);
        if (!orgUuid) throw new Error("Organization UUID missing from response.");

        const clinicResponse = await registerClinics({
          ...this.clinicData,
          OrganizationUuid: orgUuid
        });
        const clinicUuid = this.getUuid(clinicResponse, ["LocationUuid", "location_uuid", "uuid"]);
        if (!clinicUuid) throw new Error("Clinic/hospital UUID missing from response.");

        await registerUser({
          ...this.userData,
          OrganizationUuid: orgUuid,
          locations: [clinicUuid],
          clinic_name: this.clinicData.clinic_name
        });

        this.$router.push('/org');
      } catch (err) {
        this.error = err.response?.data?.detail || err.message || "An error occurred during setup.";
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style scoped>
.setup-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 24px;
}

.setup-step {
  min-height: 42px;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  background: #fff;
  color: var(--ui-muted);
  font-weight: 800;
}

.setup-step.active,
.setup-step.done {
  background: #f8fafc;
  color: #111827;
  border-color: var(--ui-border-strong);
}

.setup-step.active {
  box-shadow: 0 0 0 4px var(--ui-ring);
}

.copy-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ui-muted);
  font-size: 0.86rem;
  font-weight: 700;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.review-card {
  padding: 16px;
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  background: #f8fafc;
}

.review-card span,
.review-card small {
  display: block;
  color: var(--ui-muted);
}

.review-card strong {
  display: block;
  margin: 8px 0 4px;
  color: var(--ui-text);
}

@media (max-width: 900px) {
  .setup-steps,
  .review-grid {
    grid-template-columns: 1fr;
  }
}
</style>
